import { FunctionComponent } from 'react'
import Button from '~/components/Button'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'

type Props = {
  /** The rule's own name, read back in the sentence so the viewer sees which row they are on. */
  name: string
  /** The kind, lower case and used mid-sentence — `floor price rule`, `bidder rule`. */
  noun: string
  /** Resolves with the updated rule in every caller; the modal only waits for it to finish. */
  onArchive: () => void | Promise<unknown>
  isPending?: boolean
  /** The row's own Archive label, which the modal's two controls extend. */
  trackingPrefix: TrackingLabel
}

/**
 * The confirmation for setting a rule aside, shared by the four rule lists.
 *
 * Deliberately lighter than `ArchiveMediaModal` and `ArchiveAdUnitModal`, which make you type the
 * name back. Those archive something that is *serving*: a media or an ad unit goes out of the auction
 * on confirm, and the typed name is what forces a second read of the row. A rule cannot reach this
 * modal while any stack uses it — the server refuses, and the row's button is disabled before that —
 * so nothing stops being served here and the action is reversible from the archived list. A gate
 * heavier than the act teaches people to type past it.
 */
export const ArchiveRuleModal: FunctionComponent<Props> = ({ name, noun, onArchive, isPending, trackingPrefix }) => {
  const { close } = useGlobalModal()

  const handleArchive = async () => {
    if (isPending) {
      return
    }
    await onArchive()
    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Archive {noun}</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-3'}>
          <p>
            You are about to archive <strong>{name}</strong>.
          </p>
          <p>It will leave this list and stop being offered when a stack picks a {noun}. No stack uses it, so nothing being served changes.</p>
          <p>Archived rules stay on their own page, where an owner can bring this one back.</p>
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={`${trackingPrefix}-cancel`}>
          Cancel
        </Button>
        <Button variant={'negative'} iconName={isPending ? 'pubstack' : 'archive'} iconSpin={isPending} disabled={isPending} onClick={handleArchive} trackingLabel={`${trackingPrefix}-confirm`}>
          {isPending ? `Archiving ${noun}...` : `Archive ${noun}`}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
